import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ImageGallery from "./components/ImageGallery";
import ProductInfo from "./components/ProductInfo";
import ProductTabs from "./components/ProductTabs";
import ProductCard from "@/pages/products/components/ProductCard";
import { products, CATEGORY_LABEL } from "@/mocks/products";
import { useLocalized } from "@/lib/localize";
import { useBilingual } from "@/lib/bilingual";

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const loc = useLocalized();
  const b = useBilingual();
  const product = products.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <p className="text-stone-400 text-lg mb-4">{b("Product not found.", "找不到此產品。")}</p>
          <button
            onClick={() => navigate("/products")}
            className="bg-amber-700 text-white px-6 py-3 rounded-full text-sm font-semibold cursor-pointer"
          >
            {b("Back to Products", "返回產品列表")}
          </button>
        </div>
      </div>
    );
  }

  const relatedProducts = products
    .filter((p) => p.id !== product.id && p.category === product.category)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Route-wide key forces the entire detail content to unmount + remount
          when navigating between SKUs. Belt-and-suspenders against any stale
          React reconciliation that would otherwise leave the previous
          product's image / info visible alongside the new one. */}
      <div key={id} className="pt-20 md:pt-24">
        {/* Breadcrumb — compact on mobile */}
        <div className="max-w-screen-xl mx-auto px-6 py-4 md:py-5">
          <Link
            to="/products"
            className="md:hidden inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-700 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
            {b("Back to Products", "返回產品")}
          </Link>
          <nav className="hidden md:flex items-center gap-2 text-xs text-stone-400">
            <Link to="/" className="hover:text-amber-700 transition-colors cursor-pointer">{b("Home", "首頁")}</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/products" className="hover:text-amber-700 transition-colors cursor-pointer">{b("Products", "產品")}</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-amber-700 transition-colors cursor-pointer">
              {CATEGORY_LABEL[product.category] ? loc(CATEGORY_LABEL[product.category]) : product.category}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-stone-600 truncate max-w-xs">{loc(product.name)}</span>
          </nav>
        </div>

        {/* Product Main */}
        <div className="max-w-screen-xl mx-auto px-6 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
            <ImageGallery key={`${product.id}-gallery`} images={product.images} name={loc(product.name)} />
            <ProductInfo key={`${product.id}-info`} product={product} />
          </div>

          {/* Tabs */}
          <ProductTabs key={product.id} product={product} />
        </div>

        {/* Trust Banner */}
        <div className="bg-stone-900 py-12 mt-10">
          <div className="max-w-screen-xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {[
                {
                  icon: "ri-shield-check-line",
                  title: b("Export-grade QC", "出口級品管"),
                  desc: b(
                    "Inspection at every stage of joinery, finishing and packing.",
                    "木作、表面處理、包裝每道工序皆檢查。",
                  ),
                },
                {
                  icon: "ri-ship-line",
                  title: b("Worldwide shipping", "全球運輸"),
                  desc: b(
                    "FOB Shenzhen or Guangzhou with full export documentation support.",
                    "FOB 深圳或廣州，完整出口文件支援。",
                  ),
                },
                {
                  icon: "ri-customer-service-2-line",
                  title: b("Dedicated export team", "專屬外銷團隊"),
                  desc: b(
                    "A single point of contact in Dongguan guides your order end-to-end.",
                    "東莞單一窗口全程跟進您的訂單。",
                  ),
                },
              ].map((item) => (
                <div key={item.title} className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-700/20 text-amber-500">
                    <i className={`${item.icon} text-xl`} />
                  </div>
                  <p className="text-white font-semibold text-sm">{item.title}</p>
                  <p className="text-stone-500 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="max-w-screen-xl mx-auto px-6 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-2">
                {b("You May Also Like", "您可能也喜歡")}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900">
                {b("Related Products", "相關產品")}
              </h2>
            </div>
            <Link to="/products" className="text-stone-500 hover:text-stone-800 text-sm font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap">
              {b("View All", "查看全部")} <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop>
            {relatedProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </div>

      {/* Spacer to clear mobile sticky action bar */}
      <div className="md:hidden h-20" aria-hidden="true" />

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
